// Cloudflare Turnstile — the free, privacy-friendly CAPTCHA alternative.
//
// Rendered explicitly (not via the auto-scanning `.cf-turnstile` class) so it
// survives client-side route changes: React may mount this after the script has
// already scanned the DOM once.
//
// The widget writes its token into a hidden input named `cf-turnstile-response`
// inside this container, so FormData picks it up with no extra wiring.
//
// No site key -> renders nothing. That keeps the form working before Turnstile
// is configured, matching the server, which only verifies when TURNSTILE_SECRET
// is set. Set both or neither.
import { useEffect, useRef } from "react";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string | undefined;
      remove: (id: string) => void;
    };
  }
}

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Turnstile failed to load")));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  });
}

export default function Turnstile() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!SITE_KEY || !ref.current) return;

    let widgetId: string | undefined;
    let cancelled = false;
    const el = ref.current;

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetId = window.turnstile.render(el, { sitekey: SITE_KEY, theme: "light" });
      })
      .catch((err) => console.error(err));

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={ref} style={{ marginBottom: "12px" }} />;
}
